
import { UserInfo } from "@/models/dossier/user-info";
import { TaskAssignedUser } from "@/models/tasks/assigned-user";
import { FolderUser } from "@/models/tasks/folder-user";
import { Task, TaskStatus } from "@/models/tasks/task";
import { TaskUnreadUser } from "@/models/tasks/task-unread-user";
import { AuthService } from "@/services/auth-service";
import { DossierService } from "@/services/dossier-service";
import { TaskService } from "@/services/task-service";
import { TasksState } from "@/utils/tasks-state";
import Vue from "vue";
import { MainSocketService } from "../../services/main-socket-service";
import AppTaskPriorityChips from "./TaskPriorityChips.vue";
import AppTimeago from "@/components/common/Timeago.vue";

export default Vue.extend({
	data() {
		return {
			users: DossierService.users as UserInfo[],
			isMouseDown: false,
			dragXOffset: 0,
			dragYOffset: 0,
			dragTop: 0,
			dragLeft: 0,
		};
	},
	props: {
		value: {
			type: Object as () => Task,
			required: true
		},
		state: {
			type: Object as () => TasksState,
			required: true
		}
	},
	computed: {
		isDragging: {
			get():boolean {
				return this.state.draggedTask == this.value;
			},
			set(value:boolean) {
				this.state.draggedTask = value ? this.value : null;
			}
		},
		dragWidth: {
			get():number {
				return this.state.draggedTaskWidth;
			},
			set(value:number) {
				this.state.draggedTaskWidth = value;
			}
		},
		dragHeight: {
			get():number {
				return this.state.draggedTaskHeight;
			},
			set(value:number) {
				this.state.draggedTaskHeight = value;
			}
		},
		folderUser():FolderUser {
			return this.state.openedFolder as FolderUser;
		},
		canEdit():boolean {
			return this.folderUser.IsOwner;
		},
		showCompleteButton(): boolean {
			if (this.value.Status == TaskStatus.PENDING) {
				return true;
			}
			if (this.value.Status == 1 && this.folderUser.IsOwner) {
				return true;
			}
			return false;
		},
		hasUnread(): Boolean {
			if (!AuthService.wfUser){
				return false;
			}
			return this.value.hasUnreadMessages(AuthService.wfUser.ID);
		},
		statusClass(): string {
			if (this.value.Status == TaskStatus.PENDING) {
				return "info";
			}
			if (this.value.Status == TaskStatus.UNCONFIRMED) {
				return "warning";
			}
			return "success";
		},
		locale(): string {
			return this.$i18n.locale;
		},
		fullAssignedUsers(): UserInfo[] {
			return this.value.AssignedUsers.map(u => {
				return this.users.find(f => f.ID == u.UserID) as UserInfo;
			}).filter(f => f != undefined);
		},
		style():any {
			if (!this.isDragging) {
				return undefined;
			}
			return {
				top: this.dragTop + "px",
				left: this.dragLeft + "px",
				width: `${this.dragWidth}px`,
				height: `${this.dragHeight}px`
			};
		}
	},
	methods: {
		async completeTask() {
			await this.state.completeTask(this.value);
		},
		open() {
			if (this.state.draggedTask) return;
			this.$router.push(`/tasks/${this.value.FolderID}/${this.value.ID}`);
		},
		editTask() {
			if (this.state.draggedTask) return;
			this.$emit("edit");
		},
		onMessageUnread(data: any) {
			let m = new TaskUnreadUser(data);
			this.value.MessagesUnreadUsers.push(m);
		},
		onMessageRead(data: any) {
			let m = new TaskUnreadUser(data);
			let i = this.value.MessagesUnreadUsers.findIndex(u => u.ID == m.ID);
			if (i != -1) {
				this.value.MessagesUnreadUsers.splice(i, 1);
			}
		},
		onTaskUpdate(data: any) {
			let task = new Task(data);
			if (task.FolderID != this.value.FolderID) {
				this.state.removeTaskFromOpenedFolder(this.value);
				return;
			}
			this.value.onUpdate(task);
		},
		onUserAssigned(data: any) {
			let assignedUser = new TaskAssignedUser(data);
			this.value.AssignedUsers.push(assignedUser);
		},
		onAssignedUserRemoved(data: any) {
			let assignedUser = new TaskAssignedUser(data);
			let i = this.value.AssignedUsers.findIndex(a => a.ID == assignedUser.ID);
			if (i != -1) {
				return this.value.AssignedUsers.splice(i, 1);
			}
		},
		async updatePriority(i: number) {
			if (this.state.draggedTask) return;
			this.value.Priority = i;
			await TaskService.putTask(this.value);
		},
		onMouseDown(event:MouseEvent) {
			if (event.button != 0) return;
			if (!this.canEdit) return;
			// Fix pointerlock in firefox
			event.preventDefault();

			let rect = this.$el.getBoundingClientRect();
			this.isMouseDown = true;
			this.dragXOffset = event.pageX - rect.left;
			this.dragYOffset = event.pageY - rect.top;
			this.dragWidth = rect.width;
			this.dragHeight = rect.height;
		},
		onMouseUp(event:MouseEvent) {
			if (event.button != 0) return;
			requestAnimationFrame(() => {
				this.isMouseDown = false;
				this.isDragging = false;
			});
		},
		onThisMouseUp(event:MouseEvent) {
			if (event.button != 0) return;
			if (!this.isDragging) {
				this.open();
			}
		},
		onMouseMove(event:MouseEvent) {
			if (!this.isMouseDown) return;

			this.isDragging = true;
			this.dragTop = event.pageY - this.dragYOffset;
			this.dragLeft = event.pageX - this.dragXOffset;
		},
		onLongTouch(event:TouchEvent) {
			if (!this.canEdit) return;
			let rect = this.$el.getBoundingClientRect();
			let touch = event.touches.item(0) as Touch;
			this.dragXOffset = touch.pageX - rect.left;
			this.dragYOffset = touch.pageY - rect.top;
			this.dragWidth = rect.width;
			this.dragHeight = rect.height;
			this.dragTop = touch.pageY - this.dragYOffset;
			this.dragLeft = touch.pageX - this.dragXOffset;
			this.isDragging = true;
		},
		onTouchMove(event:TouchEvent) {
			if (!this.isDragging) return;
			let touch = event.touches.item(0) as Touch;
			this.dragTop = touch.pageY - this.dragYOffset;
			this.dragLeft = touch.pageX - this.dragXOffset;
		},
		onTouchUp(event:TouchEvent) {
			if (!this.isDragging) return;
			requestAnimationFrame(() => {
				this.isDragging = false;
			});
		}
	},
	created() {
		MainSocketService.on(`/tasks/${this.value.ID}/message/unread`, this.onMessageUnread, this);
		MainSocketService.on(`/tasks/${this.value.ID}/message/read`, this.onMessageRead, this);
		MainSocketService.on(`/task/${this.value.ID}/assigned-users/new`, this.onUserAssigned, this);
		MainSocketService.on(`/task/${this.value.ID}/assigned-users/delete`, this.onAssignedUserRemoved, this);
		MainSocketService.on(`/tasks/${this.value.ID}`, this.onTaskUpdate, this);
		window.addEventListener("mousemove", this.onMouseMove);
		window.addEventListener("mouseup", this.onMouseUp);
		window.addEventListener("touchcancel", this.onTouchUp);
		window.addEventListener("touchend", this.onTouchUp);
		window.addEventListener("touchmove", this.onTouchMove);
	},
	destroyed() {
		MainSocketService.offAll(this);
		window.removeEventListener("mouseup", this.onMouseUp);
		window.removeEventListener("touchmove", this.onTouchMove);
		window.removeEventListener("touchcancel", this.onTouchUp);
		window.removeEventListener("touchend", this.onTouchUp);
		window.removeEventListener("mousemove", this.onMouseMove);
	},
	components: {
		AppTaskPriorityChips,
		AppTimeago
	},
	i18nEx: {
		componentPrefix: "components.tasks"
	}
});
